import React from "react"
import { useAlert } from "hooks/useAlert"
import { useTranslation } from "react-i18next"

import { useAuth } from "hooks"
import * as PartnerApi from "../api/PartnerApi"
import { jsonHash } from "utils/hash"

import { Markdown } from "components/Elements/Markdown"
import FormElement from "components/Form/FormElement"
import { BC_OPERATOR } from "utils/constants"
import { isNotEmpty } from "utils/validation"

export const BilingForm = ({
    partner,
    errors,
    setErrors,
    isEdit,
    isViewMode,
    showLocalErrors,
    setShowLocalErrors,
    isAdminEditing
}) => {
    const [formFields, setFormFields] = React.useState({})
    const defaultFormEvents = React.useMemo(() => ({
        ready: true,
        // when form is in edition mode, by default the editable is set at false
        // when the form is not in edition mode, it means creation mode, the form should be editable
        editable: !isEdit,
        viewMode: isViewMode || false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isEdit])
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const formHasChanged = jsonHash(partner) !== jsonHash(formFields)

    const { entity, fetchEntity } = useAuth()
    const { alert } = useAlert()
    const { t } = useTranslation()

    const isMandateAlreadyRegistered = Boolean(formFields.bc_effective_date)

    React.useEffect(() => {
        // When partner reloaded (after await fetchData()), set data in formFields
        if(partner) setFormFields(partner)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner])

    const toggleEditableForm = () => {
        setFormEvents({
            ...formEvents,
            editable: !formEvents.editable
        })
    }

    const discardChanges = async () => {
        toggleEditableForm()
        await fetchEntity(entity.entity_id)
    }

    const handleInputChange = React.useCallback(
        (event) => {
            const target = event.target
            const value = target.type === "checkbox" ? target.checked : target.value
            const name = target.name
            setFormFields(s => ({
                ...s,
                [name]: value
            }))
        }, []
    )

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        } else {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents(s => ({
                ...s,
                ready: false
            }))

            try {
                const currentBCOperator = entity?.data_operators?.find(operator => operator.operator === BC_OPERATOR)

                const entityData = {
                    invoicing_agent_mail: formFields.bc_agent_mail.trim(),
                    invoicing_agent_lastname: formFields.bc_agent_lastname,
                    invoicing_agent_firstname: formFields.bc_agent_firstname,
                    ...(isNotEmpty(currentBCOperator.entity_data.is_member_fub) ? { is_member_fub: currentBCOperator.entity_data.is_member_fub } : {}),
                    ...(isNotEmpty(currentBCOperator.entity_data.operator_store_id) ? { operator_store_id: currentBCOperator.entity_data.operator_store_id } : {}),
                    ...(isNotEmpty(currentBCOperator.entity_data.operator_gender) ? { operator_gender: currentBCOperator.entity_data.operator_gender } : {}),
                }

                const dataOperators = [{
                    operator: BC_OPERATOR,
                    status: currentBCOperator.status,
                    entity_data: entityData
                }]

                const updatedOperators = {
                    entity_id: formFields.entity_id,
                    last_row_hash: entity.row_hash,
                    data_operators: dataOperators
                }

                const { getError } = await PartnerApi.editEntityOperators(updatedOperators)
                if (getError()) throw getError()

                // Relaoding entity is important to have the last row hash
                await fetchEntity(entity.entity_id)

                alert("success", t("edit_success", { ns: "partner" }))

                setFormEvents(s => ({
                    ...s,
                    ready: true,
                    editable: false
                }))
            } catch (error) {
                console.error(error)
                const erroMsg = error?.message?.toString()
                if (erroMsg) alert("error", erroMsg)

                setFormEvents(s => ({
                    ...s,
                    ready: true,
                    editable: true
                }))
            }
        }
    }

    return (
        <form className="c-card p-5 md:p-12 mb-7">
            <h2 className="c-h2 font-semibold mb-7 uppercase">{t("partner_info.biling.title", { ns: "partner" })}</h2>
            <div className="l-grid u-mg-negative-hz-m">
                <div className="lg:w-1/2 w-full p-5">
                    {/* agent lastname */}
                    <FormElement
                        value={formFields.bc_agent_lastname}
                        name="bc_agent_lastname"
                        type="text"
                        label={t("partner_info.biling.last_name", { ns: "partner" })}
                        className="mb-5"
                        required={!isAdminEditing}
                        readonly={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>
                <div className="lg:w-1/2 w-full p-5">
                    {/* agent firstname */}
                    <FormElement
                        value={formFields.bc_agent_firstname}
                        name="bc_agent_firstname"
                        type="text"
                        label={t("partner_info.biling.first_name", { ns: "partner" })}
                        className="mb-5"
                        required={!isAdminEditing}
                        readonly={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="lg:w-1/2 w-full p-5">
                    {/* agent mail */}
                    <FormElement
                        value={formFields.bc_agent_mail}
                        name="bc_agent_mail"
                        type="email"
                        label={t("partner_info.biling.email", { ns: "partner" })}
                        className="mb-5"
                        required={!isAdminEditing}
                        readonly={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>
            </div>

            {isMandateAlreadyRegistered &&
                <div className="c-block--alert l-col-12 text-xs mt-2 p-5">
                    <Markdown>
                        {isAdminEditing ? t("partner_info.mandate.bo_biling_not_editable_block", { ns: "partner" }) : t("partner_info.mandate.biling_not_editable_block", { ns: "partner" })}
                    </Markdown>
                </div>
            }

            {isEdit && !isViewMode && !isMandateAlreadyRegistered && 
                <div className="flex justify-end">
                    {formEvents.editable
                        ?
                            <>
                                <button
                                    type="button"
                                    className="c-btn c-btn--white-bordered c-btn--m px-12 mx-5"
                                    onClick={discardChanges}
                                >
                                    {t("global.cancel")}
                                </button>
                                <button
                                    type="submit"
                                    className="c-btn c-btn--primary c-btn--m px-12 mx-5"
                                    onClick={handleSubmit}
                                    disabled={!formEvents.ready || !formHasChanged}
                                >
                                    {t("global.save")}
                                </button>
                            </>
                        :
                            <button
                                type="button"
                                className="c-btn c-btn--primary px-12 mx-5"
                                onClick={toggleEditableForm}
                            >
                                {t("global.update")}
                            </button>
                    }
                </div>
            }
        </form>
    )
}
